import { useState } from "react";
import api from "../api";

// ── Helpers de fecha ──────────────────────────────────────────────────────────

function primerDiaMesAnterior() {
  const hoy = new Date();
  const d   = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  return d.toISOString().slice(0, 10);
}

function ultimoDiaMesAnterior() {
  const hoy = new Date();
  const d   = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
  return d.toISOString().slice(0, 10);
}

function anioAnterior() {
  return String(new Date().getFullYear() - 1);
}

// ── Badge de vencimiento ─────────────────────────────────────────────────────

function alertaVencimiento(tipo) {
  const hoy = new Date();
  const dia = hoy.getDate();
  const mes = hoy.getMonth();

  if (tipo === "mensual") {
    return dia >= 10 && dia <= 15 ? "warning" : "ok";
  }
  if (tipo === "anual_enero") {
    return mes === 0 && dia >= 26 ? "warning" : "ok";
  }
  return "ok";
}

function Badge({ tipo }) {
  const estado  = alertaVencimiento(tipo);
  const esAnual = tipo === "anual_enero";
  const label   = esAnual ? "Vence 31 enero" : "Vence día 15";
  const style   = estado === "warning"
    ? { background: "#f5a62322", color: "#f5a623", border: "1px solid #f5a62344" }
    : { background: "#00c98d22", color: "#00c98d", border: "1px solid #00c98d44" };
  return (
    <span style={{ ...style, fontSize: 11, borderRadius: 4, padding: "2px 8px", fontWeight: 600 }}>
      {estado === "warning" ? "⚠ Vence pronto" : label}
    </span>
  );
}

// ── Descarga CSV en el browser ───────────────────────────────────────────────

function descargarCSV(rows, filename) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map(r => headers.map(h => {
      const v = r[h];
      const s = String(v ?? "");
      const needsQuote = s.includes(",") || s.includes('"') || s.includes("\n");
      return needsQuote ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Tabla de preview ─────────────────────────────────────────────────────────

function TablaPreview({ data, columnas }) {
  if (!data || !data.length) {
    return <p style={{ color: "var(--text-secondary)", fontSize: 12, margin: "8px 0" }}>Sin datos para el período seleccionado.</p>;
  }
  return (
    <div style={{ overflowX: "auto", marginTop: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {columnas.map(c => (
              <th key={c.key} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)", color: "var(--text-secondary)", fontWeight: 600, whiteSpace: "nowrap" }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              {columnas.map(c => (
                <td key={c.key} style={{ padding: "5px 10px", color: "var(--text-primary)" }}>
                  {row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tarjeta genérica de reporte ──────────────────────────────────────────────

const INPUT_STYLE = {
  background: "var(--input-bg, #1e293b)",
  color: "var(--text-primary)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
  colorScheme: "dark",
};

function TarjetaReporte({
  titulo, subtitulo, tipo, tipoFiltro,
  // para tipoFiltro="mes":
  periodo, setPeriodo, periodoOpciones,
  // para tipoFiltro="rango":
  desde, hasta, setDesde, setHasta,
  // comunes:
  columnas, data, loading, error, onPreview, onDescargar,
}) {
  const rangoInvalido = tipoFiltro === "rango" && desde > hasta;

  return (
    <div style={{ background: "var(--card-bg, #111827)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "20px 24px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>{titulo}</span>
          <span style={{ color: "var(--text-secondary)", fontSize: 12, marginLeft: 10 }}>{subtitulo}</span>
        </div>
        <Badge tipo={tipo} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        {tipoFiltro === "rango" ? (
          <>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={INPUT_STYLE} />
            <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>—</span>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={INPUT_STYLE} />
          </>
        ) : (
          <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={INPUT_STYLE}>
            {periodoOpciones.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        <button
          onClick={onPreview}
          disabled={loading || rangoInvalido}
          title={rangoInvalido ? "Fecha inicial mayor que final" : ""}
          style={{ background: "var(--accent, #1a8cff)", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: loading || rangoInvalido ? "not-allowed" : "pointer", opacity: loading || rangoInvalido ? 0.7 : 1 }}
        >
          {loading ? "Cargando…" : "Vista previa"}
        </button>
        <button
          onClick={onDescargar}
          disabled={!data || !data.length}
          style={{ background: "transparent", color: "var(--text-secondary)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: !data || !data.length ? "not-allowed" : "pointer", opacity: !data || !data.length ? 0.5 : 1 }}
        >
          Descargar CSV
        </button>
      </div>

      {error && <p style={{ color: "#ff4757", fontSize: 12, marginTop: 8 }}>{error}</p>}
      {data !== null && <TablaPreview data={data} columnas={columnas} />}
    </div>
  );
}

// ── Opciones de año (solo para R3) ───────────────────────────────────────────

function opcionesAnio(n = 5) {
  const anio = new Date().getFullYear();
  return Array.from({ length: n }, (_, i) => String(anio - 1 - i));
}

// ── Columnas por reporte ─────────────────────────────────────────────────────

const COLS_R1 = [
  { key: "mes",                 label: "Mes"              },
  { key: "direccion",           label: "Dirección"        },
  { key: "categoria",           label: "Categoría"        },
  { key: "llamadas",            label: "Llamadas"         },
  { key: "minutos_cursados",    label: "Min. cursados"    },
  { key: "minutos_facturados",  label: "Min. facturados"  },
];

const COLS_R2 = [
  { key: "periodo",                  label: "Período"          },
  { key: "lineas_nuevas",            label: "Líneas nuevas"    },
  { key: "lineas_activas_acumulado", label: "Activas (acum.)"  },
  { key: "lineas_bajas",             label: "Bajas"            },
  { key: "dids_asignados",           label: "DIDs asignados"   },
];

const COLS_R3 = [
  { key: "mes",                  label: "Mes"               },
  { key: "minutos_salientes",    label: "Min. salientes"    },
  { key: "minutos_entrantes",    label: "Min. entrantes"    },
  { key: "tarifa_saliente_usd",  label: "Tarifa sal. (USD)" },
  { key: "tarifa_entrante_usd",  label: "Tarifa ent. (USD)" },
  { key: "ingreso_usd",          label: "Ingreso (USD)"     },
  { key: "egreso_usd",           label: "Egreso (USD)"      },
];

// ── Componente principal ─────────────────────────────────────────────────────

export default function ReportesARCOTEL() {
  // R1
  const [desdeR1,  setDesdeR1]  = useState(primerDiaMesAnterior());
  const [hastaR1,  setHastaR1]  = useState(ultimoDiaMesAnterior());
  const [dataR1,   setDataR1]   = useState(null);
  const [loadR1,   setLoadR1]   = useState(false);
  const [errorR1,  setErrorR1]  = useState(null);

  // R2
  const [desdeR2,  setDesdeR2]  = useState(primerDiaMesAnterior());
  const [hastaR2,  setHastaR2]  = useState(ultimoDiaMesAnterior());
  const [dataR2,   setDataR2]   = useState(null);
  const [loadR2,   setLoadR2]   = useState(false);
  const [errorR2,  setErrorR2]  = useState(null);

  // R3 (sin cambios)
  const [anioR3,   setAnioR3]   = useState(anioAnterior());
  const [dataR3,   setDataR3]   = useState(null);
  const [loadR3,   setLoadR3]   = useState(false);
  const [errorR3,  setErrorR3]  = useState(null);

  async function previewR1() {
    setLoadR1(true); setErrorR1(null);
    try {
      const res = await api.get(`/v1/arcotel/trafico?desde=${desdeR1}&hasta=${hastaR1}`);
      setDataR1(res.data.data);
    } catch (e) {
      setErrorR1(e.response?.data?.detail || "Error al cargar R1");
    } finally {
      setLoadR1(false);
    }
  }

  async function previewR2() {
    setLoadR2(true); setErrorR2(null);
    try {
      const res = await api.get(`/v1/arcotel/lineas?desde=${desdeR2}&hasta=${hastaR2}`);
      const d   = res.data;
      setDataR2([{
        periodo:                 `${d.desde} → ${d.hasta}`,
        lineas_nuevas:            d.lineas_nuevas,
        lineas_activas_acumulado: d.lineas_activas_acumulado,
        lineas_bajas:             d.lineas_bajas,
        dids_asignados:           d.dids_asignados,
      }]);
    } catch (e) {
      setErrorR2(e.response?.data?.detail || "Error al cargar R2");
    } finally {
      setLoadR2(false);
    }
  }

  async function previewR3() {
    setLoadR3(true); setErrorR3(null);
    try {
      const res = await api.get(`/v1/arcotel/interconexion?anio=${anioR3}`);
      setDataR3(res.data.data);
    } catch (e) {
      setErrorR3(e.response?.data?.detail || "Error al cargar R3");
    } finally {
      setLoadR3(false);
    }
  }

  return (
    <div style={{ padding: "24px 28px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>
        Reportes ARCOTEL
      </h2>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: "var(--text-secondary)" }}>
        Reportes regulatorios exigibles por ARCOTEL Ecuador
      </p>

      <TarjetaReporte
        titulo="R1 · Tráfico cursado/facturado"
        subtitulo="Saliente / Entrante / On-net · Vence día 15 cada mes"
        tipo="mensual"
        tipoFiltro="rango"
        desde={desdeR1}  hasta={hastaR1}
        setDesde={setDesdeR1}  setHasta={setHastaR1}
        columnas={COLS_R1}
        data={dataR1}   loading={loadR1}   error={errorR1}
        onPreview={previewR1}
        onDescargar={() => descargarCSV(dataR1, `arcotel_r1_trafico_${desdeR1}_${hastaR1}.csv`)}
      />

      <TarjetaReporte
        titulo="R2 · Líneas instaladas"
        subtitulo="Vence día 15 cada mes"
        tipo="mensual"
        tipoFiltro="rango"
        desde={desdeR2}  hasta={hastaR2}
        setDesde={setDesdeR2}  setHasta={setHastaR2}
        columnas={COLS_R2}
        data={dataR2}   loading={loadR2}   error={errorR2}
        onPreview={previewR2}
        onDescargar={() => descargarCSV(dataR2, `arcotel_r2_lineas_${desdeR2}_${hastaR2}.csv`)}
      />

      <TarjetaReporte
        titulo="R3 · Interconexión Linkotel"
        subtitulo="Ingresos y Egresos (Apéndice B) · Vence 31 enero c/año"
        tipo="anual_enero"
        tipoFiltro="mes"
        periodo={anioR3}  setPeriodo={setAnioR3}
        periodoOpciones={opcionesAnio(5)}
        columnas={COLS_R3}
        data={dataR3}   loading={loadR3}   error={errorR3}
        onPreview={previewR3}
        onDescargar={() => descargarCSV(dataR3, `arcotel_r3_interconexion_${anioR3}.csv`)}
      />
    </div>
  );
}
